import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import ConfirmationModal from "../components/ConfirmationModal";
import DateTimeFieldGroup from "../components/DateTimeFieldGroup";
import SearchableSelect from "../components/SearchableSelect";
import RecentEntityCombobox from "../components/RecentEntityCombobox";
import InlinePickInput from "../components/InlinePickInput";
import { buildTimeOptionsForWorkingStart, getDueDatePart, getDueTimePart, getDueDateForApi } from "../shared/utils/dueDateTime";
import { useEffectiveWorkingHours } from "../shared/hooks/useAccountWorkingHours";
import { validateDate, validateStartEndRange } from "../shared/utils/validation";
import { slugifyEventName, validateEventSlug } from "../shared/utils/eventSlug";
import RepeatPatternPicker from "../components/RepeatPatternPicker";
import { normalizeRepeatRule } from "../shared/utils/recurrence";
import { buildEventsManagementPath, EVENT_MGMT_TAB_EVENTS } from "../shared/utils/eventManagementTabs";
import { isPlaceholderEvent } from "../shared/utils/eventPlaceholder";
import { buildPerfectForOptions } from "../shared/utils/eventPerfectFor";
import {
  PAYMENT_MODE_INTEGRATED,
  PAYMENT_MODE_PAYMENT_LINK,
  isValidHttpUrl,
} from "../shared/utils/eventPaymentMode";
import {
  formatKidHours,
  getEventKidHoursEntered,
  getEventKidHoursRecord,
} from "../shared/utils/eventKidHours";

import { apiFetch } from "../shared/api/apiFetch";

const uiConfig = require("../uiConfig");
const { getMessageForFetchError } = require("../shared/utils/apiError");
const ErrorBanner = require("../components/ErrorBanner").default;

function EventForm() {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const location = useLocation();
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
  const account_id = userInfo.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;
  const isEdit = Boolean(eventId);
  const returnTo = (location.state?.returnTo && String(location.state.returnTo).trim())
    ? String(location.state.returnTo).trim()
    : buildEventsManagementPath({ tab: EVENT_MGMT_TAB_EVENTS });

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugManual, setSlugManual] = useState(false);
  const [description, setDescription] = useState("");
  const [event_date, setEventDate] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [start_time, setStartTime] = useState("09:00");
  const [end_time, setEndTime] = useState("10:00");
  const [repeatRule, setRepeatRule] = useState(null);
  const [location_val, setLocation] = useState("");
  const [program_id, setProgramId] = useState("");
  const [event_organizer_id, setEventOrganizerId] = useState("");
  const [backgroundImageUrl, setBackgroundImageUrl] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [isPlaceholder, setIsPlaceholder] = useState(false);
  const [maxCapacity, setMaxCapacity] = useState("25");
  const [audienceType, setAudienceType] = useState("Youth Only");
  const [perfectFor, setPerfectFor] = useState("");
  const [perfectForOptions, setPerfectForOptions] = useState(() => buildPerfectForOptions([]));
  const [eventType, setEventType] = useState("Free");
  const [priceDollars, setPriceDollars] = useState("");
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODE_PAYMENT_LINK);
  const [paymentLinkUrl, setPaymentLinkUrl] = useState("");
  const [impact_hours_entered, setImpactHoursEntered] = useState("");
  const [kidHoursRecord, setKidHoursRecord] = useState(0);
  const kidHoursTotal = useMemo(() => {
    const entered = impact_hours_entered !== "" && !Number.isNaN(Number(impact_hours_entered))
      ? Number(impact_hours_entered)
      : 0;
    return Math.round((kidHoursRecord + entered) * 100) / 100;
  }, [impact_hours_entered, kidHoursRecord]);

  function applyKidHoursFromEvent(ev) {
    const entered = getEventKidHoursEntered(ev);
    setImpactHoursEntered(entered > 0 ? String(entered) : "");
    setKidHoursRecord(getEventKidHoursRecord(ev));
  }
  const [eventStatus, setEventStatus] = useState(""); // "Open" | "Closed" – only set when editing
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [bannerError, setBannerError] = useState("");
  const [alertDialog, setAlertDialog] = useState({ open: false, title: "", message: "", onClose: null });

  const [programs, setPrograms] = useState([]);
  const [programMembers, setProgramMembers] = useState([]);
  const dateInputRef = useRef(null);
  const appliedWorkingHoursDefault = useRef(false);
  const { workingHoursStart, workingHoursEnd, loading: workingHoursLoading } = useEffectiveWorkingHours();
  const eventTimeOptions = useMemo(
    () => buildTimeOptionsForWorkingStart(workingHoursStart),
    [workingHoursStart],
  );

  const loadPrograms = useCallback(() => {
    apiFetch(serverUrl + "/getProgram", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => setPrograms(Array.isArray(data) ? data : []))
      .catch(() => setPrograms([]));
  }, [account_id, serverUrl]);

  const loadProgramMembers = useCallback(
    (gid) => {
      if (!gid) {
        setProgramMembers([]);
        return;
      }
      apiFetch(serverUrl + "/getProgramOrganizers", {
        method: "POST",
        body: JSON.stringify({ account_id, program_id: gid }),
      })
        .then((resp) => resp.json())
        .then((data) => setProgramMembers(Array.isArray(data) ? data : []))
        .catch(() => setProgramMembers([]));
    },
    [account_id, serverUrl]
  );

  useEffect(() => {
    loadPrograms();
  }, [loadPrograms]);

  // Seed + existing Perfect for values for create-or-pick suggestions
  useEffect(() => {
    apiFetch(serverUrl + "/getEvent", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => {
        setPerfectForOptions(buildPerfectForOptions(Array.isArray(data) ? data : []));
      })
      .catch(() => setPerfectForOptions(buildPerfectForOptions([])));
  }, [account_id, serverUrl]);

  useEffect(() => {
    loadProgramMembers(program_id);
    if (!program_id) setEventOrganizerId("");
  }, [program_id, loadProgramMembers]);

  // Default start/end time from account working hours when creating new event (once)
  useEffect(() => {
    if (!isEdit && !workingHoursLoading && !appliedWorkingHoursDefault.current) {
      appliedWorkingHoursDefault.current = true;
      setStartTime(workingHoursStart);
      setEndTime(workingHoursEnd);
    }
  }, [isEdit, workingHoursLoading, workingHoursStart, workingHoursEnd]);

  // Load event when editing (from state or fetch)
  useEffect(() => {
    if (!isEdit) return;
    const fromState = location.state?.event;
    if (fromState && fromState._id === eventId) {
      setName(fromState.name || "");
      setSlug((fromState.slug || slugifyEventName(fromState.name || "")).trim().toLowerCase());
      setSlugManual(Boolean((fromState.slug || "").trim()));
      setDescription(fromState.description || "");
      const fromPlaceholder = isPlaceholderEvent(fromState);
      setIsPlaceholder(fromPlaceholder);
      setEventDate(getDueDatePart(fromState.start_datetime) || (fromPlaceholder ? "" : dayjs().format("YYYY-MM-DD")));
      setStartTime(getDueTimePart(fromState.start_datetime) || "09:00");
      setEndTime(getDueTimePart(fromState.end_datetime) || "10:00");
      setRepeatRule(normalizeRepeatRule(fromState.repeat_rule, getDueDatePart(fromState.start_datetime)));
      setLocation(fromState.location || "");
      setProgramId(fromState.program_id || "");
      setEventOrganizerId(fromState.event_organizer_id || "");
      setBackgroundImageUrl(fromState.background_image_url || "");
      setVisibility(fromState.visibility === "private" ? "private" : "public");
      setMaxCapacity(fromState.max_capacity != null && fromState.max_capacity !== "" ? String(fromState.max_capacity) : "25");
      setAudienceType(["Youth Only", "Adult Only", "All Ages"].includes(fromState.audience_type) ? fromState.audience_type : "Youth Only");
      setPerfectFor((fromState.perfect_for != null ? String(fromState.perfect_for) : "").trim());
      setEventType(fromPlaceholder ? "Free" : (fromState.event_type === "Paid" ? "Paid" : "Free"));
      setPriceDollars(fromState.price_cents != null && fromState.price_cents !== "" ? String(Number(fromState.price_cents) / 100) : "");
      setPaymentMode(fromState.payment_mode === PAYMENT_MODE_INTEGRATED ? PAYMENT_MODE_INTEGRATED : PAYMENT_MODE_PAYMENT_LINK);
      setPaymentLinkUrl((fromState.payment_link_url != null ? String(fromState.payment_link_url) : "").trim());
      applyKidHoursFromEvent(fromState);
      setEventStatus(fromState.status === "Closed" ? "Closed" : "Open");
      loadProgramMembers(fromState.program_id || "");
      setLoading(false);
      return;
    }
    apiFetch(serverUrl + "/getEvent", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        const ev = list.find((e) => String(e._id) === String(eventId));
        if (ev) {
          setName(ev.name || "");
          setSlug((ev.slug || slugifyEventName(ev.name || "")).trim().toLowerCase());
          setSlugManual(Boolean((ev.slug || "").trim()));
          setDescription(ev.description || "");
          const fromPlaceholder = isPlaceholderEvent(ev);
          setIsPlaceholder(fromPlaceholder);
          setEventDate(getDueDatePart(ev.start_datetime) || (fromPlaceholder ? "" : dayjs().format("YYYY-MM-DD")));
          setStartTime(getDueTimePart(ev.start_datetime) || "09:00");
          setEndTime(getDueTimePart(ev.end_datetime) || "10:00");
          setRepeatRule(normalizeRepeatRule(ev.repeat_rule, getDueDatePart(ev.start_datetime)));
          setLocation(ev.location || "");
          setProgramId(ev.program_id || "");
          setEventOrganizerId(ev.event_organizer_id || "");
          setBackgroundImageUrl(ev.background_image_url || "");
          setVisibility(ev.visibility === "private" ? "private" : "public");
          setMaxCapacity(ev.max_capacity != null && ev.max_capacity !== "" ? String(ev.max_capacity) : "25");
          setAudienceType(["Youth Only", "Adult Only", "All Ages"].includes(ev.audience_type) ? ev.audience_type : "Youth Only");
          setPerfectFor((ev.perfect_for != null ? String(ev.perfect_for) : "").trim());
          setEventType(fromPlaceholder ? "Free" : (ev.event_type === "Paid" ? "Paid" : "Free"));
          setPriceDollars(ev.price_cents != null && ev.price_cents !== "" ? String(Number(ev.price_cents) / 100) : "");
          setPaymentMode(ev.payment_mode === PAYMENT_MODE_INTEGRATED ? PAYMENT_MODE_INTEGRATED : PAYMENT_MODE_PAYMENT_LINK);
          setPaymentLinkUrl((ev.payment_link_url != null ? String(ev.payment_link_url) : "").trim());
          applyKidHoursFromEvent(ev);
          setEventStatus(ev.status === "Closed" ? "Closed" : "Open");
          loadProgramMembers(ev.program_id || "");
        } else {
          setBannerError("Event not found.");
        }
        setLoading(false);
      })
      .catch((err) => {
        setBannerError(getMessageForFetchError(err));
        setLoading(false);
      });
  }, [isEdit, eventId, account_id, serverUrl, loadProgramMembers, location.state]);

  function handleSubmit(e) {
    e.preventDefault();
    const nameVal = (name || "").trim();
    if (!nameVal) {
      setAlertDialog({
        open: true,
        title: "Validation",
        message: "Name is required.",
        onClose: null,
      });
      return;
    }

    const slugVal = (slug || slugifyEventName(nameVal) || "").trim().toLowerCase();
    const slugCheck = validateEventSlug(slugVal);
    if (!slugCheck.valid) {
      setAlertDialog({
        open: true,
        title: "Validation",
        message: slugCheck.message || "A valid URL slug is required.",
        onClose: null,
      });
      return;
    }

    const programIdVal = (program_id || "").trim();
    if (!programIdVal) {
      setAlertDialog({
        open: true,
        title: "Validation",
        message: "Program is required.",
        onClose: null,
      });
      return;
    }

    const eventOrganizerIdVal = (event_organizer_id || "").trim();
    if (!eventOrganizerIdVal) {
      setAlertDialog({
        open: true,
        title: "Validation",
        message: "Hosted By is required.",
        onClose: null,
      });
      return;
    }

    // Safety check: if programMembers are loaded, ensure the selected lead belongs to the selected program.
    if (Array.isArray(programMembers) && programMembers.length > 0) {
      const leadInProgram = programMembers.some((m) => String(m._id) === eventOrganizerIdVal);
      if (!leadInProgram) {
        setAlertDialog({
          open: true,
          title: "Validation",
          message: "Hosted By must belong to the selected Program.",
          onClose: null,
        });
        return;
      }
    }

    const dateStr = (event_date || "").trim();
    let start_datetime;
    let end_datetime;
    if (isPlaceholder) {
      if (dateStr) {
        const dateCheck = validateDate(dateStr);
        if (!dateCheck.valid) {
          setAlertDialog({
            open: true,
            title: "Validation",
            message: dateCheck.message || "Invalid date.",
            onClose: null,
          });
          return;
        }
        const startStr = (start_time || "").trim() || "09:00";
        const endStr = (end_time || "").trim() || "10:00";
        const rangeCheck = validateStartEndRange(
          `${dateStr}T${startStr}`,
          `${dateStr}T${endStr}`,
          { granularity: "time", allowEqual: true }
        );
        if (!rangeCheck.valid) {
          setAlertDialog({
            open: true,
            title: "Validation",
            message: rangeCheck.message || "End time must be on or after start time.",
            onClose: null,
          });
          return;
        }
        start_datetime = getDueDateForApi(dateStr, startStr);
        end_datetime = getDueDateForApi(dateStr, endStr);
      }
    } else {
      const requiredDate = dateStr || dayjs().format("YYYY-MM-DD");
      const dateCheck = validateDate(requiredDate);
      if (!dateCheck.valid) {
        setAlertDialog({
          open: true,
          title: "Validation",
          message: dateCheck.message || "Invalid date.",
          onClose: null,
        });
        return;
      }
      const startStr = (start_time || "").trim() || "09:00";
      const endStr = (end_time || "").trim() || "10:00";
      const rangeCheck = validateStartEndRange(
        `${requiredDate}T${startStr}`,
        `${requiredDate}T${endStr}`,
        { granularity: "time", allowEqual: true }
      );
      if (!rangeCheck.valid) {
        setAlertDialog({
          open: true,
          title: "Validation",
          message: rangeCheck.message || "End time must be on or after start time.",
          onClose: null,
        });
        return;
      }
      start_datetime = getDueDateForApi(requiredDate, startStr);
      end_datetime = getDueDateForApi(requiredDate, endStr);
    }

    if (!isPlaceholder && eventType === "Paid") {
      const priceNum = Number(priceDollars);
      if (priceDollars === "" || Number.isNaN(priceNum) || priceNum <= 0) {
        setAlertDialog({
          open: true,
          title: "Validation",
          message: "Price must be greater than zero for Paid events.",
          onClose: null,
        });
        return;
      }
      if (paymentMode !== PAYMENT_MODE_INTEGRATED) {
        const linkUrl = (paymentLinkUrl || "").trim();
        if (!linkUrl) {
          setAlertDialog({
            open: true,
            title: "Validation",
            message: "Payment link URL is required for Paid events using Payment Link mode.",
            onClose: null,
          });
          return;
        }
        if (!isValidHttpUrl(linkUrl)) {
          setAlertDialog({
            open: true,
            title: "Validation",
            message: "Payment link URL must be a valid http(s) URL.",
            onClose: null,
          });
          return;
        }
      }
    }

    setSaving(true);
    setBannerError("");

    const body = {
      account_id,
      name: nameVal,
      slug: slugVal,
      description: (description || "").trim(),
      ...(start_datetime ? { start_datetime } : {}),
      ...(end_datetime ? { end_datetime } : {}),
      repeat_rule: isPlaceholder ? null : repeatRule,
      location: (location_val || "").trim() || undefined,
      program_id: programIdVal,
      event_organizer_id: eventOrganizerIdVal,
      background_image_url: (backgroundImageUrl || "").trim() || undefined,
      visibility: visibility === "private" ? "private" : "public",
      is_placeholder: Boolean(isPlaceholder),
      max_capacity: maxCapacity !== "" && !Number.isNaN(Number(maxCapacity)) && Number(maxCapacity) >= 0 ? Math.floor(Number(maxCapacity)) : undefined,
      audience_type: ["Youth Only", "Adult Only", "All Ages"].includes(audienceType) ? audienceType : "Youth Only",
      perfect_for: (perfectFor || "").trim(),
      event_type: isPlaceholder ? "Free" : (eventType === "Paid" ? "Paid" : "Free"),
      price_cents: !isPlaceholder && eventType === "Paid" && priceDollars !== "" && !Number.isNaN(Number(priceDollars)) && Number(priceDollars) > 0
        ? Math.round(Number(priceDollars) * 100)
        : undefined,
      ...(!isPlaceholder && eventType === "Paid"
        ? paymentMode === PAYMENT_MODE_INTEGRATED
          ? { payment_mode: PAYMENT_MODE_INTEGRATED }
          : {
              payment_mode: PAYMENT_MODE_PAYMENT_LINK,
              payment_link_url: (paymentLinkUrl || "").trim(),
            }
        : {}),
    };
    // Entered kid hours are only editable for closed events (record hours come from Record & Close).
    if (isEdit && eventStatus === "Closed") {
      body.impact_hours_entered = impact_hours_entered !== "" && !Number.isNaN(Number(impact_hours_entered))
        ? Number(impact_hours_entered)
        : 0;
    }

    const url = isEdit ? serverUrl + "/updateEvent" : serverUrl + "/createEvent";
    if (isEdit) body._id = eventId;

    apiFetch(url, {
      method: "POST",
      body: JSON.stringify(body),
    })
      .then((resp) => {
        return resp.text().then((text) => ({ ok: resp.ok, text }));
      })
      .then(({ ok, text }) => {
        if (ok) {
          let displayMsg = text;
          try {
            const j = JSON.parse(text);
            if (j && typeof j.message === "string") displayMsg = j.message;
            else if (j && j._id) displayMsg = "Created successfully";
          } catch {
            /* plain text */
          }
          setAlertDialog({
            open: true,
            title: isEdit ? "Event updated" : "Event created",
            message: displayMsg,
            onClose: () => navigate(returnTo, { state: location.state || {} }),
          });
        } else {
          setBannerError(text || "Request failed");
        }
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)))
      .finally(() => setSaving(false));
  }

  const inputStyle = {
    padding: "8px 12px",
    fontSize: "13px",
    border: "1px solid #dee2e6",
    borderRadius: "4px",
    minWidth: "80px",
    boxSizing: "border-box",
    background: "#fff",
    color: "#212529",
    width: "100%",
  };

  const rowStyle = { display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" };
  const fieldStyle = { flex: "1", minWidth: "120px" };

  if (loading) {
    return (
      <div style={{ padding: "24px" }}>
        <p>Loading event…</p>
      </div>
    );
  }

  return (
    <div className="page-container event-form-page">
    <div className="event-form" style={{ maxWidth: "900px", margin: "0 auto" }}>
      <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "12px" }}>
        <button
          type="button"
          onClick={() => navigate(returnTo, { state: location.state || {} })}
          style={{
            padding: "6px 12px",
            fontSize: "14px",
            border: "1px solid #6c757d",
            borderRadius: "4px",
            background: "#fff",
            color: "#6c757d",
            cursor: "pointer",
          }}
        >
          ← Back to Event Management
        </button>
        <h2 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 600, color: "#212529" }}>
          {isEdit ? "Edit Event" : "New Event"}
        </h2>
      </div>

      <ErrorBanner message={bannerError} onDismiss={() => setBannerError("")} />

      <ConfirmationModal
        open={alertDialog.open}
        title={alertDialog.title || "Message"}
        message={alertDialog.message}
        mode="alert"
        onConfirm={() => {
          const fn = alertDialog.onClose;
          setAlertDialog({ open: false, title: "", message: "", onClose: null });
          if (fn) fn();
        }}
        onCancel={() =>
          setAlertDialog({ open: false, title: "", message: "", onClose: null })
        }
      />

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <div style={rowStyle}>
          <label className="form-row-label">Name *</label>
          <div style={fieldStyle}>
            <input
              type="text"
              value={name}
              placeholder="Event name"
              required
              onChange={(e) => {
                const next = e.target.value;
                setName(next);
                if (!slugManual) setSlug(slugifyEventName(next));
              }}
              style={inputStyle}
            />
          </div>
        </div>

        <div style={rowStyle}>
          <label className="form-row-label" htmlFor="event-form-slug">Public URL</label>
          <div style={fieldStyle}>
            <input
              id="event-form-slug"
              type="text"
              value={slug}
              placeholder="summer-picnic"
              onChange={(e) => {
                setSlugManual(true);
                setSlug(e.target.value.trim().toLowerCase());
              }}
              style={inputStyle}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        </div>
        <p style={{ margin: 0, marginLeft: "130px", fontSize: "12px", color: "#6c757d" }}>
          Shareable link: <code>/e/{slug || "your-slug"}</code>
          {" "}(lowercase letters, numbers, hyphens)
        </p>

        <div style={rowStyle}>
          <label className="form-row-label">Placeholder</label>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <label className="panel-radio-option" style={{ margin: 0 }}>
              <input
                type="checkbox"
                checked={isPlaceholder}
                onChange={(e) => {
                  const next = e.target.checked;
                  setIsPlaceholder(next);
                  if (next) {
                    setEventType("Free");
                    setPriceDollars("");
                    setPaymentMode(PAYMENT_MODE_PAYMENT_LINK);
                    setPaymentLinkUrl("");
                    setRepeatRule(null);
                  }
                }}
              />
              Tag as placeholder (advertise; no registration yet)
            </label>
          </div>
        </div>
        <p style={{ margin: 0, marginLeft: "130px", fontSize: "12px", color: "#6c757d" }}>
          Placeholder events appear on the public calendar without requiring date, time, or location.
          Registration is disabled until you clear this flag and set the schedule.
        </p>

        <div style={rowStyle}>
          <label className="form-row-label">Date & time{isPlaceholder ? "" : " *"}</label>
          <div style={{ ...fieldStyle, minWidth: 0 }}>
            <DateTimeFieldGroup
              layout="form-row"
              date={event_date}
              onDateChange={setEventDate}
              startTime={start_time}
              onStartTimeChange={setStartTime}
              endTime={end_time}
              onEndTimeChange={setEndTime}
              timeOptions={eventTimeOptions}
              required={!isPlaceholder}
              dateInputRef={dateInputRef}
              showDatePickerOnClick
            >
              {!isPlaceholder && (
                <div className="form-datetime-row__repeat">
                  <span className="panel-inline-label">Repeat</span>
                  <RepeatPatternPicker
                    anchorDate={event_date}
                    value={repeatRule}
                    onChange={setRepeatRule}
                    compact
                    dropdownPortal
                  />
                </div>
              )}
            </DateTimeFieldGroup>
          </div>
        </div>
        {isPlaceholder && (
          <p style={{ margin: 0, marginLeft: "130px", fontSize: "12px", color: "#6c757d" }}>
            Optional for placeholders. Leave blank to show TBA on the calendar.
          </p>
        )}

        <div style={rowStyle}>
          <label className="form-row-label" htmlFor="event-form-location">
            Location
          </label>
          <div style={fieldStyle}>
            <RecentEntityCombobox
              id="event-form-location"
              accountId={account_id}
              entityType="LOCATION"
              value={location_val}
              onChange={setLocation}
              placeholder="Address or conference link"
              style={inputStyle}
            />
          </div>
        </div>
        <p style={{ margin: 0, marginLeft: "130px", fontSize: "12px", color: "#6c757d" }}>
          Recent & frequent: suggestions are ranked by past events. Type to filter, or enter any new address or link.
        </p>

        <div style={rowStyle}>
          <label className="form-row-label">Max Capacity</label>
          <div style={{ ...fieldStyle, maxWidth: "120px" }}>
            <input
              type="text"
              min={0}
              value={maxCapacity}
              placeholder="Optional"
              onChange={(e) => setMaxCapacity(e.target.value)}
              className="input-number-no-spin"
              style={inputStyle}
            />
          </div>
        </div>
        <p style={{ margin: 0, marginLeft: "130px", fontSize: "12px", color: "#6c757d" }}>
          Optional. Leave blank for no limit on registrations.
        </p>

        <div style={rowStyle}>
          <label className="form-row-label">Audience Type</label>
          <div style={{ ...fieldStyle, maxWidth: "200px" }}>
            <select
              value={audienceType}
              onChange={(e) => setAudienceType(e.target.value)}
              style={inputStyle}
            >
              <option value="Youth Only">Youth Only</option>
              <option value="Adult Only">Adult Only</option>
              <option value="All Ages">All Ages</option>
            </select>
          </div>
        </div>
        <p style={{ margin: 0, marginLeft: "130px", fontSize: "12px", color: "#6c757d" }}>
          Used when recording & closing the event to show the right participation fields.
        </p>
        <p style={{ margin: 0, marginLeft: "130px", fontSize: "12px", color: "#6c757d" }}>
          For Youth Only events, registration will also collect parent/guardian name, email, and phone.
        </p>

        <div style={rowStyle}>
          <label className="form-row-label">Perfect for</label>
          <div style={fieldStyle}>
            <InlinePickInput
              value={perfectFor}
              onChange={setPerfectFor}
              options={perfectForOptions}
              placeholder="Type or pick existing"
            />
          </div>
        </div>
        <p style={{ margin: 0, marginLeft: "130px", fontSize: "12px", color: "#6c757d" }}>
          Optional. Who this event is best for — e.g. 6th - 12th Graders. Shown on event cards and registration.
        </p>

        <div style={rowStyle}>
          <label className="form-row-label">Event Type</label>
          <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <label className="panel-radio-option">
              <input
                type="radio"
                name="event_type"
                value="Free"
                checked={eventType === "Free"}
                onChange={() => {
                  setEventType("Free");
                  setPaymentMode(PAYMENT_MODE_PAYMENT_LINK);
                  setPaymentLinkUrl("");
                }}
                disabled={isPlaceholder}
              />
              Free
            </label>
            <label className="panel-radio-option">
              <input
                type="radio"
                name="event_type"
                value="Paid"
                checked={eventType === "Paid"}
                onChange={() => {
                  setEventType("Paid");
                  if (paymentMode !== PAYMENT_MODE_INTEGRATED) {
                    setPaymentMode(PAYMENT_MODE_PAYMENT_LINK);
                  }
                }}
                disabled={isPlaceholder}
              />
              Paid
            </label>
          </div>
        </div>
        {eventType === "Paid" && !isPlaceholder && (
          <>
            <div style={rowStyle}>
              <label className="form-row-label">Price ($)</label>
              <div style={{ ...fieldStyle, maxWidth: "120px" }}>
                <input
                  type="text"
                  min={0}
                  step="0.01"
                  value={priceDollars}
                  placeholder="0.00"
                  onChange={(e) => setPriceDollars(e.target.value)}
                  className="input-number-no-spin"
                  style={inputStyle}
                />
              </div>
            </div>
            {paymentMode !== PAYMENT_MODE_INTEGRATED && (
              <div style={rowStyle}>
                <label className="form-row-label">Payment link URL</label>
                <div style={fieldStyle}>
                  <input
                    type="url"
                    value={paymentLinkUrl}
                    placeholder="https://buy.stripe.com/..."
                    onChange={(e) => setPaymentLinkUrl(e.target.value)}
                    style={inputStyle}
                  />
                </div>
              </div>
            )}
          </>
        )}
        <p style={{ margin: 0, marginLeft: "130px", fontSize: "12px", color: "#6c757d" }}>
          {isPlaceholder
            ? "Placeholder events do not accept registration (including paid)."
            : eventType === "Free"
              ? "Registration is free."
              : paymentMode === PAYMENT_MODE_INTEGRATED
                ? "This Paid event uses Integrated Stripe Checkout (Stripe Connection). Payment status updates automatically after checkout."
                : "Paid events use a Stripe Payment Link. Paste the buy.stripe.com URL; price should match the link amount. Staff mark participants Paid after payment."}
        </p>

        <div style={rowStyle}>
          <label className="form-row-label">Background image</label>
          <div style={fieldStyle}>
            <input
              type="url"
              value={backgroundImageUrl}
              placeholder="https://example.com/your-event-image.jpg"
              onChange={(e) => setBackgroundImageUrl(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>
        <p style={{ margin: 0, marginLeft: "130px", fontSize: "12px", color: "#6c757d" }}>
          Optional. Paste a direct image link (usually ending in .jpg or .png), for example from your own site or a royalty-free source like Pexels.
          Use the image address, not the web page URL. If left blank, event cards still show a default background image.
        </p>

        <div style={rowStyle}>
          <label className="form-row-label">Visibility</label>
          <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <label className="panel-radio-option">
              <input
                type="radio"
                name="visibility"
                value="public"
                checked={visibility === "public"}
                onChange={() => setVisibility("public")}
              />
              Public
            </label>
            <label className="panel-radio-option">
              <input
                type="radio"
                name="visibility"
                value="private"
                checked={visibility === "private"}
                onChange={() => setVisibility("private")}
              />
              Private
            </label>
          </div>
        </div>
        <p style={{ margin: 0, marginLeft: "130px", fontSize: "12px", color: "#6c757d" }}>
          Public events appear on the event calendar. Private events are hidden from the calendar.
        </p>

        <p style={{ margin: 0, marginLeft: "130px", fontSize: "12px", color: "#6c757d" }}>
          Location suggestions are based on your recently used locations.
        </p>

        <div style={rowStyle}>
          <label className="form-row-label">Program *</label>
          <div className="form-row-fields">
            <div className="form-row-fields__control">
              <SearchableSelect
                value={program_id}
                onChange={(v) => {
                  const nextProgramId = String(v || "");
                  setProgramId(nextProgramId);
                  setEventOrganizerId("");
                }}
                options={(programs || []).map((g) => ({
                  value: String(g._id),
                  label: String(g?.name || "Program"),
                }))}
                placeholder="Select program…"
                searchPlaceholder="Search program…"
                allowEmpty={true}
                emptyLabel="—"
                maxVisibleItems={4}
                dropdownPortal={true}
                style={inputStyle}
              />
            </div>
            <div className="form-row-fields__segment">
              <span className="panel-inline-label">Hosted By *</span>
              <div className="form-row-fields__control">
                <SearchableSelect
                  value={event_organizer_id}
                  onChange={(v) => setEventOrganizerId(String(v || ""))}
                  options={(programMembers || []).map((m) => {
                    const label = [m?.first_name, m?.last_name].filter(Boolean).join(" ").trim() || "Member";
                    return {
                      value: String(m._id),
                      label,
                      keywords: [m?.first_name, m?.last_name, m?.email_id, m?.email, m?.phone_number].filter(Boolean).join(" "),
                    };
                  })}
                  placeholder="Select organizer…"
                  searchPlaceholder="Search organizer…"
                  allowEmpty={true}
                  emptyLabel="—"
                  maxVisibleItems={4}
                  dropdownPortal={true}
                  style={inputStyle}
                />
              </div>
            </div>
          </div>
        </div>

        {isEdit && eventStatus === "Closed" && (
          <>
            <div style={rowStyle}>
              <label className="form-row-label">Kid hours (entered)</label>
              <div style={{ ...fieldStyle, maxWidth: "120px" }}>
                <input
                  type="text"
                  min={0}
                  step="0.01"
                  value={impact_hours_entered}
                  placeholder="0"
                  onChange={(e) => setImpactHoursEntered(e.target.value)}
                  className="input-number-no-spin"
                  style={inputStyle}
                />
              </div>
            </div>
            <div style={rowStyle}>
              <label className="form-row-label">Kid hours (record)</label>
              <div style={{ ...fieldStyle, maxWidth: "120px", paddingTop: "6px" }}>
                {formatKidHours(kidHoursRecord)}
              </div>
            </div>
            <div style={rowStyle}>
              <label className="form-row-label">Kid hours (total)</label>
              <div style={{ ...fieldStyle, maxWidth: "120px", paddingTop: "6px" }}>
                {formatKidHours(kidHoursTotal)}
              </div>
            </div>
            <p style={{ margin: 0, marginLeft: "130px", fontSize: "12px", color: "#6c757d" }}>
              Record hours come from Record &amp; Close. Entered hours are added for old events or adjustments. Total is used for Operating Plan reporting.
            </p>
          </>
        )}

        <div style={rowStyle}>
          <label className="form-row-label">Description</label>
          <div style={fieldStyle}>
            <textarea
              value={description}
              placeholder="Detailed description, agenda, or notes…"
              onChange={(e) => setDescription(e.target.value)}
              rows={6}
              style={{ ...inputStyle, minHeight: "120px", resize: "vertical" }}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: "8px 20px",
              fontSize: "14px",
              border: "1px solid #0d6efd",
              borderRadius: "4px",
              backgroundColor: "#0d6efd",
              color: "#fff",
              cursor: saving ? "not-allowed" : "pointer",
              fontWeight: 500,
            }}
          >
            {saving ? "Saving…" : isEdit ? "Update Event" : "Create Event"}
          </button>
          <button
            type="button"
            onClick={() => navigate(returnTo, { state: location.state || {} })}
            style={{
              padding: "8px 20px",
              fontSize: "14px",
              border: "1px solid #6c757d",
              borderRadius: "4px",
              background: "#fff",
              color: "#6c757d",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
    </div>
  );
}

export default EventForm;
